<template>
  <component
    :is="component"
    :data-source="currentLayerDataSource"
    :layer="currentLayer"
    :lamp-id="lampId"
    :is-subfeature="isLampSubfeatureLayer"
  ></component>
</template>

<script>
import { get } from 'vuex-pathify';

import { capitalize } from '@/assets/js/utility';
import camelCase from 'lodash/camelCase';

/**
 * Automatically register components which names start with FeatureCard
 * (except FeatureCardHandler and FeatureCardData).
 */
const components = {};
const requireComponent = require.context(
  // The relative path of the components folder
  '@/components',
  // Whether or not to look in subfolders
  false,
  // The regular expression used to match base component filenames
  /FeatureCard(?!Data|Handler)\w+\.(vue|js)$/
);
requireComponent.keys().forEach(fileName => {
  // Get component config
  const componentConfig = requireComponent(fileName);

  // Get PascalCase name of component
  const componentName = capitalize(
    camelCase(
      // Gets the file name regardless of folder depth
      fileName
        .split('/')
        .pop()
        .replace(/\.\w+$/, '')
    )
  );
  components[componentName] = componentConfig.default;
});

export default {
  name: 'FeatureCardHandler',
  components,
  computed: {
    lampId() {
      return this.$route.query.lamp_id;
    },
    currentLayer: get('layers/currentLayer'),
    currentLayerId: get('layers/currentLayer@id'),
    currentLayerDataSource: get('layers/currentLayer@data_source_name'),
    hydrantsDataSourceName: get('admin/modulesMapping@hydrants_inspections.hydrants_datasource_name'),
    vehiclesLayerDataSource: get('admin/modulesMapping@vehicles.datasource_name'),
    partitionsDataSourceName: get('admin/modulesMapping@billing.partitions_datasource_name'),
    zdmStreetLampDataSourceName: get('admin/modulesMapping@zdm_data.datasources.street_lamp.datasource'),
    zdmCabinetDataSourceName: get('admin/modulesMapping@zdm_data.datasources.cabinet.datasource'),
    documentationDataSourceName: get('admin/modulesMapping@documentation.attachment_datasource_name'),
    polesLayerGeomId: get('admin/modulesMapping@zdm_data.layers.pole'),
    pickArmsLayerGeomId: get('admin/modulesMapping@zdm_data.layers.pick_arm'),
    lightHeadsLayerGeomId: get('admin/modulesMapping@zdm_data.layers.head'),
    scadaDataSourceName: get('admin/modulesMapping@scada.datasource_name'),
    masterplanSquaresDataSourceName: get('admin/modulesMapping@squares.squares_datasource_name'),
    masterplanIluminationDataSourceName: get('admin/modulesMapping@illumination.illumination_datasource_name'),
    masterplanStreetsModulesMapping: get('admin/modulesMapping@masterplan_streets'),
    masterplanRpDataSourceName: get('admin/modulesMapping@masterplan_rp.datasource'),
    videoDataSourceName: get('admin/modulesMapping@video.selected_datasource_name'),
    komadresDataSource: get('admin/modulesMapping@komadres_orders.komadres_orders_datasource'),
    historyDataSourceName: get('admin/modulesMapping@history.history_datasource_name'),
    scadaThreeDataSourceName: get('admin/modulesMapping@scada3.selected_datasource_name'),
    lampSubfeaturesLayersIds() {
      return [this.polesLayerGeomId, this.pickArmsLayerGeomId, this.lightHeadsLayerGeomId];
    },
    isLampSubfeatureLayer() {
      return this.lampId && this.lampSubfeaturesLayersIds.includes(this.currentLayerId);
    },
    fid() {
      return this.$route?.params?.fid;
    },
    fidIsNew() {
      return (this.fid || this.fid === 0) && this.fid === 'new';
    },
    masterplanStreetsLayers() {
      const masterplanStreetsLayer = this.masterplanStreetsModulesMapping.street_layer_id;
      const masterplanStreetsLayers = JSON.parse(
        JSON.stringify(this.masterplanStreetsModulesMapping.street_object_layers_id)
      );
      masterplanStreetsLayers.push(masterplanStreetsLayer);
      return masterplanStreetsLayers;
    },
    component() {
      const dict = {
        [this.vehiclesLayerDataSource]: 'FeatureCardVehicle',
        [this.hydrantsDataSourceName]: 'FeatureCardHydrant',
        [this.partitionsDataSourceName]: 'FeatureCardPartition',
        [this.zdmStreetLampDataSourceName]: 'FeatureCardZdmLamp',
        [this.zdmCabinetDataSourceName]: 'FeatureCardZdmCabinet',
        [this.documentationDataSourceName]: 'FeatureCardDocument',
        [this.scadaDataSourceName]: 'FeatureCardScada',
        [this.masterplanSquaresDataSourceName]: 'FeatureCardMasterplanSquares',
        [this.masterplanIluminationDataSourceName]: 'FeatureCardMasterplanIlumination',
        [this.masterplanRpDataSourceName]: 'FeatureCardMasterplanRp',
        [this.videoDataSourceName]: 'FeatureCardVideo',
        [this.komadresDataSource]: 'FeatureCardKomadres',
        [this.historyDataSourceName]: 'FeatureCardHistory',
        [this.scadaThreeDataSourceName]: 'FeatureCardScadaThree',
      };
      if (dict[this.currentLayerDataSource]) {
        return dict[this.currentLayerDataSource];
      }
      if (this.isLampSubfeatureLayer) {
        return 'FeatureCardZdmLamp';
      }
      if (this.currentLayer?.tree_view && !this.fidIsNew) {
        if (this.masterplanStreetsLayers.includes(this.currentLayerId)) {
          return 'FeatureCardStreetTreeview';
        }
        return 'FeatureCardTreeview';
      }
      return 'FeatureCardBasic';
    },
  },
};
</script>

<style lang="scss" scoped></style>
